import {Directive, ElementRef} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Dropdown, RadioGroup} from '@fluentui/web-components';


/**
 * Value accessor for Fluent UI RadioGroup web component.
 *
 * This directive enables Angular forms to interact with the Fluent UI RadioGroup component.
 * It implements ControlValueAccessor to bridge Angular's form API and the non-standard behavior
 * of the Fluent UI web component.
 *
 * Note: The Fluent UI RadioGroup behaves mostly as expected, so this accessor simply proxies
 * value changes between Angular and the component.
 */
@Directive({
    selector: '[ngFluentRadioGroupValueAccessor]',
    standalone: true,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: FluentRadioGroupValueAccessor,
        multi: true
    }]
})
export class FluentRadioGroupValueAccessor implements ControlValueAccessor {
    onChanged: Function = () => {};
    onTouched: Function = () => {};

    private fluentRadioGroup: RadioGroup;

    constructor(private elementRef: ElementRef) {
        this.fluentRadioGroup = elementRef.nativeElement as RadioGroup;
        this.fluentRadioGroup.onchange = (event: Event) => {
            const value = (event.target as RadioGroup).value;
            this.onChanged(value);
        }
    }

    registerOnChange(fn: Function) {
        this.onChanged = (value: string) => {
            fn(value);
        }
    }

    registerOnTouched(fn: Function) {
        this.onTouched = fn;
    }

    writeValue(value: any): void {
        this.fluentRadioGroup.value = value;
    }
}


/**
 * Value accessor for Fluent UI Combobox web component.
 *
 * This directive enables Angular forms to interact with the Fluent UI Combobox component.
 * It implements ControlValueAccessor to bridge Angular's form API and the non-standard behavior
 * of the Fluent UI Combobox.
 *
 * There are timing problems ... Angular often calls the writeValue() method before the Fluent UI
 * Combobox is ready. To work around this, the writeValue() method is delayed by some milliseconds
 * until the Combobox is ready. This is not ideal, but it works for now.
 */
@Directive({
    selector: '[ngFluentComboboxValueAccessor]',
    standalone: true,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: FluentComboboxValueAccessor,
        multi: true
    }]
})
export class FluentComboboxValueAccessor implements ControlValueAccessor {
    onChanged: Function = () => {};
    onTouched: Function = () => {};

    pendingSetValue: any = undefined;

    static readonly DEFAULT_DELAY_MS = 10;

    private fluentCombobox: Dropdown;

    constructor(private elementRef: ElementRef) {
        this.fluentCombobox = elementRef.nativeElement as Dropdown;
        this.fluentCombobox.onchange = (event: Event) => {
            const value = (event.target as Dropdown).value;
            this.onChanged(value);
        }
    }

    registerOnChange(fn: Function) {
        this.onChanged = fn;
    }

    registerOnTouched(fn: Function) {
        this.onTouched = fn;
    }

    /**
     * Clear any pending delayed writeValue() call.
     */
    private clearTimeout() {
        if (this.pendingSetValue !== undefined) {
            clearTimeout(this.pendingSetValue);
            this.pendingSetValue = undefined;
        }
    }

    /**
     * Schedule a delayed writeValue() call to the Fluent UI Combobox. Note that this can
     * result in repeated scheduling, because the writeValue() method is called and this method
     * then may schedule another writeValue() call.
     * @param value
     * @private
     */
    private scheduleSetValue(value: any) {
        this.clearTimeout();
        this.pendingSetValue = setTimeout(() => {
            this.writeValue(value);
        }, FluentComboboxValueAccessor.DEFAULT_DELAY_MS);
    }

    /**
     * Write a value to the Fluent UI Combobox. This method is called by Angular when the form
     * value changes. We test whether the Combobox is ready and delay the writeValue() call if not.
     */
    writeValue(value: any): void {
        this.clearTimeout();
        if (this.fluentCombobox.control != null && this.fluentCombobox.options.length > 0) {
            this.fluentCombobox.value = value;
        } else {
            this.scheduleSetValue(value);
        }
    }
}
